import { Eyebrow, LinkButton } from '@/components/ui';
import { cn } from '@/lib/format';
import { CheckIcon, SparkIcon } from '@/lib/icons';

type Variant = 'outline' | 'secondary' | 'primary';

const PLANS: Array<{
  id: 'free' | 'premium';
  name: string;
  tagline: string;
  price: number;
  features: string[];
  cta: string;
  variant: Variant;
  note: string;
}> = [
  {
    id: 'free',
    name: 'Free',
    tagline: 'The full tracker, free forever',
    price: 0,
    features: [
      'The Grid — sets, reps, weight, distance and time',
      'The programme builder and every track',
      'Goals, personal records, streaks and history',
      'The block summary at the end of every block',
      'Generate your plan on the free trial',
      'Web, iOS, Android and watchOS — one synced log',
      'Offline logging and automatic sync',
      'CSV / JSON export of everything',
    ],
    cta: 'Start free',
    variant: 'outline',
    note: 'No card, no logging limits — the tracker is free forever.',
  },
  {
    id: 'premium',
    name: 'Premium',
    tagline: 'The AI coach keeps reading your log',
    price: 3.99,
    features: [
      'Everything in Free',
      'A specialist AI coach for your track',
      'Weekly review with the next week prescribed',
      'Progressive overload and fatigue detection with the evidence',
      'Deload triggers with automatic volume reduction',
      'Injury-aware adjustments as your log changes',
    ],
    cta: 'Start 14 days free',
    variant: 'primary',
    note: 'Billed monthly. Cancel any time in two taps.',
  },
];

const TRACKS = ['Strength', 'Bodybuilding', 'Conditioning', 'Performance', 'Wellness', 'General'];

export function Pricing() {
  return (
    <section id="pricing" className="border-b border-hairline bg-page py-20">
      <div className="mx-auto max-w-[1200px] 2xl:max-w-[1440px] min-[1920px]:max-w-[1720px] px-4 sm:px-6">
        <div className="mb-8 flex flex-col items-start gap-6">
          <Eyebrow>Pricing</Eyebrow>
          <h2 className="font-display text-h2 font-black uppercase leading-tight tracking-[-0.01em] text-ink md:text-display-2">
            Free tracker. An AI coach when you want one.
          </h2>
          <p className="max-w-[74ch] text-body-lg text-ink-muted">
            The full tracker is free forever, because a log you cannot afford is a log you will not keep. Generate
            your plan on the free trial, then subscribe when you want the AI coach to keep reading your training and
            rewriting it as you go.
          </p>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">AI coaches available</span>
            {TRACKS.map((track) => (
              <span
                key={track}
                className="rounded-pill border border-hairline px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.08em] text-ink"
              >
                {track}
              </span>
            ))}
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {PLANS.map((plan) => {
            const isPremium = plan.id === 'premium';

            return (
              <div
                key={plan.id}
                className={cn(
                  'relative flex flex-col overflow-hidden rounded-card border border-hairline bg-surface p-7',
                  isPremium ? 'shadow-modal' : 'shadow-card',
                )}
              >
                {isPremium ? <span aria-hidden="true" className="absolute inset-x-0 top-0 h-[3px] bg-accent" /> : null}

                <div className="flex items-baseline justify-between gap-3">
                  <h3 className="font-display text-[20px] font-black uppercase tracking-[-0.01em] text-ink">
                    {plan.name}
                  </h3>
                  {isPremium ? (
                    <span className="inline-flex items-center gap-1 rounded-pill border border-accent bg-accent/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.08em] text-accent">
                      <SparkIcon className="text-xs" /> Most useful
                    </span>
                  ) : null}
                </div>
                <p className="mt-1 text-[13px] text-ink-muted">{plan.tagline}</p>

                <p className="l2f-tabular mt-6 font-mono text-4xl font-bold text-ink">
                  {plan.price === 0 ? 'Free' : `£${plan.price.toFixed(2)}`}
                  <span className="ml-2 text-[13px] font-semibold text-ink-muted">
                    {plan.price === 0 ? 'forever' : '/ month'}
                  </span>
                </p>

                <ul className="mt-6 flex flex-1 flex-col gap-3">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2.5 text-[13px] leading-snug text-ink">
                      <CheckIcon className={cn('mt-[2px] shrink-0 text-sm', isPremium ? 'text-accent' : 'text-ink')} />
                      {feature}
                    </li>
                  ))}
                </ul>

                <LinkButton
                  variant={plan.variant}
                  size="lg"
                  href={isPremium ? '#/plan/new' : '#/signup'}
                  className="mt-7"
                >
                  {plan.cta}
                </LinkButton>
                <p className="mt-3 text-[11px] text-ink-muted">{plan.note}</p>
              </div>
            );
          })}
        </div>

        <p className="mt-6 max-w-[88ch] text-[11px] text-ink-muted">
          Every programme — on the free tier and with Premium — is gym-based and indoor: no running, no outdoor
          sessions, nothing that depends on the weather. Lift2Fit is a training log, not a medical device; nothing
          here is medical advice.
        </p>
      </div>
    </section>
  );
}
